/**
 * Recuperação automática de sessão Supabase quebrada.
 *
 * CONTEXTO
 * O cliente Supabase tem `autoRefreshToken: true`. Em condições normais ele
 * renova o JWT antes de expirar. Mas em cenários reais (e frequentes) o
 * refresh falha em segundo plano:
 *
 *   - Computador hibernou e perdeu o ciclo de refresh enquanto o token
 *     expirava (caso típico do Douglas: logou às 22h, abriu às 11h).
 *   - GoTrue teve um soluço (deploy, falha regional, restart do Supabase)
 *     no exato segundo que o refresh ia rodar.
 *   - Network blip durante o refresh.
 *
 * Quando isso acontece, o token velho fica preso no localStorage e o front
 * tenta usá-lo em loop. Resultado visível pro operador: "Servidor
 * temporariamente indisponível" depois de 15s, e F5 não resolve porque
 * o token podre persiste entre reloads.
 *
 * SOLUÇÃO
 * Detectar essa condição (`isAuthDeadError`) e forçar um logout LIMPO que:
 *   1. Apaga todas as chaves do supabase-js do storage (sb-* / supabase*)
 *   2. Redireciona pra tela de login com `?reason=...` (a AuthForm
 *      mostra mensagem amigável "Sua sessão expirou — entre novamente").
 *
 * Não usamos `supabase.auth.signOut()` aqui porque essa chamada PODE TRAVAR
 * quando o refresh token está corrompido — ela depende do mesmo client que
 * está com problema. Pulamos direto pro storage cleanup.
 */

/**
 * Limpa storage do supabase-js e redireciona pra `/login?reason=<reason>`.
 * Idempotente: se a página já está em `/login`, só limpa storage.
 */
export function forceCleanLogout(reason: string = 'session_expired'): void {
  console.warn(`[auth-recovery] forçando logout limpo: ${reason}`);
  try {
    const purge = (storage: Storage) => {
      const keys = Object.keys(storage);
      for (const k of keys) {
        if (k.startsWith('sb-') || k.toLowerCase().includes('supabase')) {
          storage.removeItem(k);
        }
      }
    };
    purge(localStorage);
    purge(sessionStorage);
  } catch (e) {
    console.error('[auth-recovery] erro limpando storage:', e);
  }
  // Evita loop: se já estamos em /login, só recarrega
  if (window.location.pathname === '/login') {
    window.location.reload();
    return;
  }
  const url = new URL(window.location.origin + '/login');
  url.searchParams.set('reason', reason);
  window.location.replace(url.toString());
}

/**
 * Detecta erros de supabase-js que indicam sessão morta / refresh token
 * quebrado. Cobre os padrões observados em produção.
 */
export function isAuthDeadError(err: any): boolean {
  if (!err) return false;
  const code = String((err as any)?.code || '').toLowerCase();
  const msg = String((err as any)?.message || '').toLowerCase();
  if (code === 'refresh_token_not_found') return true;
  if (code === 'refresh_token_already_used') return true;
  if (msg.includes('refresh_token_not_found')) return true;
  if (msg.includes('refresh token not found')) return true;
  if (msg.includes('refresh token already used')) return true;
  if (msg.includes('invalid refresh token')) return true;
  if (msg.includes('jwt expired')) return true;
  return false;
}

/**
 * Texto amigável pra mostrar na AuthForm quando vier com `?reason=...`.
 */
export function reasonToMessage(reason: string | null | undefined): string | null {
  if (!reason) return null;
  switch (reason) {
    case 'refresh_failed':
      return 'Sua sessão expirou. Faça login novamente para continuar.';
    case 'getSession_error':
      return 'Não conseguimos validar sua sessão. Entre novamente.';
    case 'session_expired':
      return 'Sessão expirada. Entre novamente.';
    case 'boot_timeout':
      return 'O sistema demorou para responder. Entre novamente para recarregar sua sessão.';
    case 'manual':
      return null;
    default:
      return 'Faça login novamente.';
  }
}
